/**
 * The table on a canvas. Mounted once; `update` redraws whatever the view and
 * the pick decide, `hover` and `drag` touch only what they must.
 *
 * The canvas only draws. Every input arrives through the HTML hit layer laid
 * over it, which uses the same rectangles (`layout.ts`), so this class never
 * has to know what a click means — and nothing here can accept one.
 *
 * Frames are drawn on demand: a table that is not moving costs nothing. The
 * ticker runs only while live targets breathe, and not at all when the reader
 * has asked for reduced motion.
 */
import type { Color, GameEvent, PlayerId, View } from '@trajan/engine';
import { Application, Container, FillPattern, Graphics, Rectangle } from 'pixi.js';
import { ringPoint } from '../mat/geometry';
import type { MatShow } from '../mat/matShow';
import { boardPieces, staticBoard, type Register, type StackMarks } from './draw/board';
import { matPieces, staticMat } from './draw/mat';
import { actionMarker, label, setTextResolution } from './draw/pieces';
import { tableau } from './draw/tableau';
import { fontsReady } from './fonts';
import type { TargetId } from './ids';
import {
  MAT_AT,
  SHIP_W,
  TABLE_H,
  TABLE_W,
  centreOf,
  demandSlot,
  provinceCentre,
  senateStep,
  shipRect,
  siteRect,
  siteRows,
  slotRect,
  type Point,
  type Rect,
} from './layout';
import { woodTexture } from './materials';
import { Motion, easeInOut, easeOut } from './motion';
import { ACCENT, LIVE, PICKED, seatPaint } from './palette';

export type Tone = 'go' | 'choose' | 'picked';

export interface SceneInput {
  view: View;
  /** Whose tableau is on show. */
  seat: PlayerId;
  mat: MatShow;
  /** Targets that can be acted on now, and how each should look. */
  live: ReadonlyMap<TargetId, Tone>;
  /** Where every target is, from `tableLayout`. */
  rects: ReadonlyMap<TargetId, Rect>;
  /** Cards lifted out of the hand. */
  raised: ReadonlySet<string>;
  /** "Your", or a seat's name with an apostrophe, for the tableau's caption. */
  owner: string;
  /** What each ship would pay for the cards chosen, while some are. */
  ships: { shipId: string; vp: number | null }[] | null;
  /** At setup, the stacks taken from: in hand, or put on a slot. */
  stacks?: StackMarks | undefined;
  /**
   * What happened since the last events handed over, with where in the log
   * they start — so a batch is played once however often the table redraws.
   */
  events?: { from: number; list: readonly GameEvent[] } | undefined;
}

const ROUND: (id: TargetId) => boolean = (id) =>
  id.startsWith('province:') || id === 'italia' || id.startsWith('marker:') || id === 'vp';

const reducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export class TableScene {
  private readonly app = new Application();
  private readonly world = new Container();
  private readonly table = new Container();
  private readonly fixed = new Container();
  private readonly pieces = new Container();
  private readonly glow = new Container();
  private readonly hoverLayer = new Container();
  private readonly fx = new Container();
  private readonly dragLayer = new Container();
  /** Pieces gliding and markers being sown: finished when the table is redrawn under them. */
  private readonly glides = new Motion();
  /** Points floating up: left to finish however often the table is redrawn. */
  private readonly effects = new Motion();
  private registry = new Map<TargetId, Container>();
  private keyed = new Map<string, Container>();
  private played = -1;
  private fixedKey = '';
  private input: SceneInput | null = null;
  private hovered: TargetId | null = null;
  private lifted: { obj: Container; y: number } | null = null;
  private frame = 0;
  private pulse = 0;
  private started = false;
  private destroyed = false;
  private resolution = 0;

  /** Build the canvas inside `host`. Resolves once fonts are in and the first frame can be drawn. */
  async mount(host: HTMLElement, width: number, height: number): Promise<void> {
    await fontsReady();
    await this.app.init({
      width: Math.max(1, width),
      height: Math.max(1, height),
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      autoStart: false,
      preference: 'webgl',
    });
    if (this.destroyed) {
      this.app.destroy(true);
      return;
    }
    this.app.canvas.setAttribute('aria-hidden', 'true');
    this.app.canvas.classList.add('table-canvas');
    host.appendChild(this.app.canvas);
    this.world.addChild(
      this.table,
      this.fixed,
      this.pieces,
      this.glow,
      this.hoverLayer,
      this.fx,
      this.dragLayer,
    );
    this.app.stage.addChild(this.world);
    const wood = new Graphics()
      .rect(0, 0, TABLE_W, TABLE_H)
      .fill(new FillPattern(woodTexture(), 'repeat'));
    const vignette = new Graphics()
      .rect(0, 0, TABLE_W, TABLE_H)
      .fill({ color: 0x000000, alpha: 0.12 });
    this.table.addChild(wood, vignette);
    this.app.ticker.add(() => this.tick());
    this.started = true;
    this.resize(width, height);
  }

  /** Fit the table to `width` × `height` CSS pixels. */
  resize(width: number, height: number): void {
    if (!this.started) return;
    this.app.renderer.resize(Math.max(1, width), Math.max(1, height));
    const scale = width / TABLE_W;
    this.world.scale.set(scale);
    // Text is rasterised once: at the scale it will be seen at, so it stays sharp.
    const resolution = Math.round(scale * (window.devicePixelRatio || 1) * 4) / 4;
    if (resolution !== this.resolution) {
      this.resolution = resolution;
      setTextResolution(resolution);
      this.fixedKey = '';
      if (this.input) this.update(this.input);
    }
    this.requestFrame();
  }

  update(input: SceneInput): void {
    this.input = input;
    if (!this.started) return;
    const key = `${input.view.playerCount}/${input.view.time.length}/${this.resolution}`;
    if (key !== this.fixedKey) {
      this.fixedKey = key;
      destroyChildren(this.fixed);
      this.fixed.addChild(staticBoard(input.view.playerCount, input.view.time.length), staticMat());
    }

    // Where every piece with an identity stands before it is redrawn — mid-glide
    // if it is still moving — so it can glide on from there to its new place.
    const before = new Map<string, Point>();
    for (const [key, obj] of this.keyed) if (!obj.destroyed) before.set(key, this.inWorld(obj));
    this.glides.finish();
    destroyChildren(this.pieces);
    this.lifted = null;
    this.registry = new Map();
    this.keyed = new Map();
    const register: Register = Object.assign(
      (id: TargetId, obj: Container) => {
        this.registry.set(id, obj);
      },
      {
        piece: (key: string, obj: Container) => {
          this.keyed.set(key, obj);
        },
      },
    );
    this.pieces.addChild(boardPieces(input.view, register, input.stacks));
    if (input.ships) this.pieces.addChild(shipBadges(input.ships, input.view));
    this.pieces.addChild(matPieces(input.mat, register));
    const p = input.view.players[input.seat];
    if (p) this.pieces.addChild(tableau(p, register, { raised: input.raised, owner: input.owner }));

    destroyChildren(this.glow);
    for (const [id, tone] of input.live) {
      // A chosen card is lifted out of the hand and ringed where it now is, by the tableau.
      if (tone === 'picked' && input.raised.has(id.slice('card:'.length))) continue;
      const r = input.rects.get(id);
      if (r) this.glow.addChild(rim(id, r, tone));
    }
    const fresh = input.events && input.events.from !== this.played ? input.events : null;
    if (input.events) this.played = input.events.from;
    // Nothing is set moving on a hidden page: nobody would see it, and the table
    // should already be settled when they come back to it.
    if (!reducedMotion() && !document.hidden) {
      this.glide(before);
      if (fresh) this.play(fresh.list, input);
    }
    this.hover(this.hovered, true);
    this.syncTicker();
    this.requestFrame();
  }

  /** A piece's position in table units. */
  private inWorld(obj: Container): Point {
    const g = obj.getGlobalPosition();
    const s = this.world.scale.x || 1;
    return { x: g.x / s, y: g.y / s };
  }

  /** Every piece that was somewhere else a moment ago glides from there to here. */
  private glide(before: ReadonlyMap<string, Point>): void {
    for (const [key, obj] of this.keyed) {
      // Markers are sown round the ring instead, when a tray is picked up.
      if (key.startsWith('marker-')) continue;
      const was = before.get(key);
      if (!was) continue;
      const now = this.inWorld(obj);
      const dx = was.x - now.x;
      const dy = was.y - now.y;
      const far = Math.hypot(dx, dy);
      if (far < 1.5) continue;
      const home = { x: obj.x, y: obj.y };
      obj.position.set(home.x + dx, home.y + dy);
      this.glides.add(Math.min(720, 240 + far * 0.7), (p) => {
        const left = 1 - easeInOut(p);
        obj.position.set(home.x + dx * left, home.y + dy * left);
      });
    }
  }

  /** What the events since last time look like: markers sown, points floating up where they were won. */
  private play(list: readonly GameEvent[], input: SceneInput): void {
    let delay = 0;
    const float = (at: Point, vp: number, seat: number) => {
      if (vp === 0) return;
      this.float(at, vp, seat, delay);
      delay += 260;
    };
    const rows = siteRows(input.view.playerCount);
    for (const e of list) {
      switch (e.type) {
        case 'TrayMoved':
          if (e.player === input.seat) this.sow(e, input);
          break;
        case 'ProvinceOccupied':
          float(provinceCentre(e.province), e.vp, e.player);
          break;
        case 'WorkerBuilt':
          float(centreOf(siteRect(e.site, rows)), e.vp, e.player);
          break;
        case 'CardsShipped': {
          const i = input.view.board.ships.findIndex((s) => s.id === e.ship);
          if (i >= 0) float(centreOf(shipRect(i)), e.vp, e.player);
          break;
        }
        case 'SenateAdvanced': {
          const s = senateStep(e.to);
          float({ x: s.x + s.w / 2, y: s.y - 16 }, e.vp, e.player);
          break;
        }
        case 'TrajanTileAccomplished':
          if (e.player === input.seat) float(centreOf(slotRect(e.slot)), e.vp, e.player);
          break;
        case 'DemandPenalty':
          float(centreOf(demandSlot(1)), e.vp, e.player);
          break;
      }
    }
  }

  /** Points won or lost, rising from where it happened in the scorer's colour. */
  private float(at: Point, vp: number, seat: number, delay: number): void {
    const t = label(`${vp > 0 ? '+' : '\u2212'}${Math.abs(vp)}`, 26, {
      face: 'display',
      fill: 0xfff4e0,
      halo: seatPaint(seat).dark,
    });
    t.position.set(at.x, at.y);
    t.alpha = 0;
    this.fx.addChild(t);
    this.effects.add(
      1600,
      (p) => {
        t.y = at.y - 40 * easeOut(p);
        t.alpha = p < 0.12 ? p / 0.12 : p > 0.7 ? (1 - p) / 0.3 : 1;
        t.scale.set(0.85 + 0.25 * easeOut(Math.min(1, p * 4)));
      },
      { delay, done: () => t.destroy() },
    );
  }

  /**
   * A tray picked up: its markers leave it one by one and travel clockwise round
   * the ring, each dropping into the tray it lands in — the move the forecast
   * promised, played out.
   */
  private sow(e: Extract<GameEvent, { type: 'TrayMoved' }>, input: SceneInput): void {
    const player = input.view.players[e.player];
    if (!player) return;
    const on = (i: number): Point => {
      const p = ringPoint(i);
      return { x: MAT_AT.x + p.x, y: MAT_AT.y + p.y };
    };
    const drops: number[][] = Array.from({ length: 6 }, () => []);
    for (let k = 0; k < e.moved; k++) drops[(e.source + k + 1) % 6]!.push(k);
    drops.forEach((ks, tray) => {
      const count = player.trays[tray]?.length ?? 0;
      ks.forEach((k, j) => {
        // A tray's markers are kept in the order they landed: the newest are last.
        const obj = this.keyed.get(`marker-${tray}-${count - ks.length + j}`);
        if (!obj) return;
        const home = { x: obj.x, y: obj.y };
        const start = on(e.source);
        obj.position.set(start.x, start.y);
        obj.alpha = 0;
        this.glides.add(
          300 + 120 * (k + 1),
          (p) => {
            obj.alpha = 1;
            const q = easeInOut(p);
            if (q < 0.82) {
              const a = on(e.source + (k + 1) * (q / 0.82));
              obj.position.set(a.x, a.y);
            } else {
              const end = on(e.source + k + 1);
              const r = (q - 0.82) / 0.18;
              obj.position.set(end.x + (home.x - end.x) * r, end.y + (home.y - end.y) * r);
            }
          },
          { delay: k * 120 },
        );
      });
    });
  }

  /** Light up what the pointer or the focus is on, and lift it a little if it is live. */
  hover(id: TargetId | null, force = false): void {
    if (!force && id === this.hovered) return;
    this.hovered = id;
    if (this.lifted) {
      this.lifted.obj.y = this.lifted.y;
      this.lifted = null;
    }
    destroyChildren(this.hoverLayer);
    const input = this.input;
    if (id && input) {
      const r = input.rects.get(id);
      const tone = input.live.get(id);
      if (r) this.hoverLayer.addChild(hoverRim(id, r, tone !== undefined));
      const obj = this.registry.get(id);
      if (obj && tone && tone !== 'picked') {
        this.lifted = { obj, y: obj.y };
        obj.y -= 2;
      }
    }
    this.requestFrame();
  }

  /** A marker being dragged, drawn under the pointer (design units), or nothing. */
  drag(d: { color: Color; x: number; y: number } | null): void {
    destroyChildren(this.dragLayer);
    if (d) {
      const m = actionMarker(d.color, 19);
      m.position.set(d.x, d.y);
      this.dragLayer.addChild(m);
    }
    this.requestFrame();
  }

  /**
   * The table as it stands, or one region of it (in table units), as a PNG
   * data URL — for looking at it closely while developing.
   */
  snapshot(region?: Rect, resolution = 1): Promise<string> {
    const s = this.world.scale.x;
    return this.app.renderer.extract.base64({
      target: this.app.stage,
      resolution,
      ...(region
        ? { frame: new Rectangle(region.x * s, region.y * s, region.w * s, region.h * s) }
        : {}),
    });
  }

  destroy(): void {
    this.destroyed = true;
    if (!this.started) return;
    cancelAnimationFrame(this.frame);
    this.app.ticker.stop();
    this.app.destroy(true, { children: true });
    this.started = false;
  }

  private get breathing(): boolean {
    return (this.input?.live.size ?? 0) > 0 && !reducedMotion();
  }

  private syncTicker(): void {
    const run = this.breathing || this.glides.busy || this.effects.busy;
    if (run && !this.app.ticker.started) this.app.ticker.start();
    if (!run && this.app.ticker.started) {
      this.app.ticker.stop();
      this.glow.alpha = 1;
      this.requestFrame();
    }
  }

  private tick(): void {
    // A long pause (a hidden tab, a stopped ticker) is not an instruction to jump.
    const ms = Math.min(50, this.app.ticker.deltaMS);
    this.pulse += ms / 1000;
    this.glow.alpha = this.breathing ? 0.62 + 0.38 * Math.sin(this.pulse * 2.6) ** 2 : 1;
    this.glides.tick(ms);
    this.effects.tick(ms);
    this.app.render();
    if (!this.breathing && !this.glides.busy && !this.effects.busy) this.syncTicker();
  }

  private requestFrame(): void {
    if (!this.started || this.app.ticker.started) return;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      if (this.started) this.app.render();
    });
  }
}

function destroyChildren(c: Container): void {
  for (const child of c.removeChildren()) child.destroy({ children: true });
}

/** The breathing rim of a live target — round for round things — or the ring of a picked one. */
function rim(id: TargetId, r: Rect, tone: Tone): Container {
  if (tone === 'picked') {
    const g = new Graphics();
    if (ROUND(id)) {
      const c = centreOf(r);
      g.circle(c.x, c.y, Math.min(r.w, r.h) / 2 + 3).stroke({ width: 3, color: PICKED });
    } else {
      g.roundRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6, 9).stroke({ width: 3, color: PICKED });
    }
    return g;
  }
  if (ROUND(id)) {
    const c = centreOf(r);
    const radius = id.startsWith('marker:')
      ? r.w / 2 + 2
      : id.startsWith('province:') || id === 'italia'
        ? 26
        : r.w / 2;
    const g = new Graphics();
    for (let k = 3; k >= 1; k--) {
      g.circle(c.x, c.y, radius + k * 2).stroke({ width: 2.2, color: LIVE, alpha: 0.18 * (4 - k) });
    }
    g.circle(c.x, c.y, radius).stroke({ width: 2, color: 0xfff0c0, alpha: 0.95 });
    return g;
  }
  const g = new Graphics();
  for (let k = 3; k >= 1; k--) {
    const o = k * 2.2;
    g.roundRect(r.x - o, r.y - o, r.w + 2 * o, r.h + 2 * o, 9 + o).stroke({
      width: 2.4,
      color: LIVE,
      alpha: 0.18 * (4 - k),
    });
  }
  g.roundRect(r.x - 1, r.y - 1, r.w + 2, r.h + 2, 9).stroke({
    width: 2,
    color: 0xfff0c0,
    alpha: 0.95,
  });
  return g;
}

function hoverRim(id: TargetId, r: Rect, live: boolean): Graphics {
  const g = new Graphics();
  const color = live ? 0xffffff : 0xfff3d6;
  const width = live ? 2.4 : 1.4;
  const alpha = live ? 1 : 0.7;
  if (ROUND(id)) {
    const c = centreOf(r);
    const radius = id.startsWith('province:') || id === 'italia' ? 28 : Math.min(r.w, r.h) / 2 + 4;
    g.circle(c.x, c.y, radius).stroke({ width, color, alpha });
  } else {
    g.roundRect(r.x - 4, r.y - 4, r.w + 8, r.h + 8, 10).stroke({ width, color, alpha });
  }
  return g;
}

/** Over each ship, what it would pay for the cards chosen — or a cross if it will not take them. */
function shipBadges(ships: { shipId: string; vp: number | null }[], view: View): Container {
  const c = new Container();
  for (const s of ships) {
    const i = view.board.ships.findIndex((x) => x.id === s.shipId);
    if (i < 0) continue;
    const r = shipRect(i);
    const g = new Graphics();
    const x = r.x + SHIP_W - 12;
    const y = r.y + 10;
    g.circle(x, y, 13)
      .fill(s.vp === null ? 0x5a5550 : ACCENT)
      .stroke({ width: 1.5, color: 0xfff1e0 });
    c.addChild(g);
    const t = label(s.vp === null ? '✗' : `${s.vp}`, s.vp === null ? 13 : 12, {
      face: s.vp === null ? 'text' : 'display',
      fill: 0xfff1e0,
      weight: '700',
    });
    t.position.set(x, y + 0.5);
    c.addChild(t);
  }
  return c;
}
